import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { WorkOrderFilterCategory, WorkOrderFilterCondition, WorkOrderFilterJoin } from '../../../enums/workOrderFilter';
import { WorkOrderSort } from '../../../enums/workOrderSort';
import { WorkOrderStatus } from '../../../enums/workOrderStatus';
import type { WorkOrderFilterRule, WorkOrderListItem } from '../../../types/InfrastructureManagerTypes';
import { CALENDAR_DAY_KEY_FORMAT, STATUS_ORDER } from './constants';

type OrderComparator = (a: WorkOrderListItem, b: WorkOrderListItem) => number;

const byDate =
  (key: 'dateAssigned' | 'updatedAt', direction: 1 | -1): OrderComparator =>
  (a, b) =>
    (Date.parse(a[key]) - Date.parse(b[key])) * direction;

const byTitle =
  (direction: 1 | -1): OrderComparator =>
  (a, b) =>
    a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' }) * direction;

const SORT_COMPARATORS: Record<WorkOrderSort, OrderComparator> = {
  [WorkOrderSort.DATE_ASSIGNED]: byDate('dateAssigned', 1),
  [WorkOrderSort.NEWEST_EDITED]: byDate('updatedAt', -1),
  [WorkOrderSort.OLDEST_EDITED]: byDate('updatedAt', 1),
  [WorkOrderSort.TITLE_ASC]: byTitle(1),
  [WorkOrderSort.TITLE_DESC]: byTitle(-1),
};

// Matches the title, the "WO n" number, asset names and assigned contacts.
export const filterBySearch = (orders: WorkOrderListItem[], term: string) => {
  const query = term.trim().toLowerCase();
  if (!query) return orders;
  return orders.filter((order) =>
    [order.title, order.number === undefined ? '' : `WO ${order.number}`, ...order.assets.map((a) => a.displayValue), ...order.contacts.map((c) => c.name)].some(
      (text) => text.toLowerCase().includes(query),
    ),
  );
};

const isWithin = (date: string | undefined, [from, to]: [string, string]) =>
  !!date && !dayjs(date).isBefore(from, 'day') && !dayjs(date).isAfter(to, 'day');

const matchesRule = (order: WorkOrderListItem, { category, condition, value }: WorkOrderFilterRule) => {
  const range = Array.isArray(value) ? value : undefined;
  const option = typeof value === 'string' ? value : undefined;
  const matched = (() => {
    switch (category) {
      case WorkOrderFilterCategory.LAYER:
        return order.assets.some((asset) => String(asset.layerId) === option);
      case WorkOrderFilterCategory.ASSIGNED_TO:
        return order.contacts.some((contact) => String(contact.id) === option);
      case WorkOrderFilterCategory.SUBMITTED_BY:
        return order.createdBy !== null && String(order.createdBy.id) === option;
      case WorkOrderFilterCategory.DATE_SUBMITTED:
        return !!range && isWithin(order.dateAssigned, range);
      case WorkOrderFilterCategory.DATE_SCHEDULED:
        return !!range && order.scheduleDates.some((date) => isWithin(date, range));
      case WorkOrderFilterCategory.DATE_COMPLETED:
        return !!range && isWithin(order.completedAt, range);
      case WorkOrderFilterCategory.DATE_EDITED:
        return !!range && isWithin(order.updatedAt, range);
      default:
        return true;
    }
  })();
  return condition === WorkOrderFilterCondition.IS_NOT ? !matched : matched;
};

// Rules apply strictly left to right, no precedence: A AND B OR C = (A AND B) OR C.
export const applyFilters = (orders: WorkOrderListItem[], rules: WorkOrderFilterRule[]) =>
  rules.length
    ? orders.filter((order) =>
        rules.reduce(
          (result, rule, i) =>
            i === 0 ? matchesRule(order, rule) : rule.join === WorkOrderFilterJoin.OR ? result || matchesRule(order, rule) : result && matchesRule(order, rule),
          true,
        ),
      )
    : orders;

export const groupByStatus = (orders: WorkOrderListItem[]) => {
  const groups = Object.fromEntries(STATUS_ORDER.map((status) => [status, []])) as unknown as Record<
    WorkOrderStatus,
    WorkOrderListItem[]
  >;
  for (const order of orders) groups[order.status].push(order);
  return groups;
};

export const sortOrders = (orders: WorkOrderListItem[], sort: WorkOrderSort) => [...orders].sort(SORT_COMPARATORS[sort]);

// The days an order sits on in the calendar, ascending: a completed order on the day it was
// completed; any other status on each of its schedule days (individual days, not a range).
// Submitted, On Hold and Archived orders keep whatever schedule they have; none means no calendar day.
export const getCalendarDays = (order: WorkOrderListItem): Dayjs[] =>
  (order.status === WorkOrderStatus.COMPLETED && order.completedAt ? [order.completedAt] : order.scheduleDates)
    .map((date) => dayjs(date).startOf('day'))
    .sort((a, b) => a.valueOf() - b.valueOf());

const isDayInRange = (day: Dayjs, rangeStart: Dayjs, rangeEnd: Dayjs) => !day.isBefore(rangeStart) && !day.isAfter(rangeEnd);

export const isOrderInRange = (order: WorkOrderListItem, rangeStart: Dayjs, rangeEnd: Dayjs) =>
  getCalendarDays(order).some((day) => isDayInRange(day, rangeStart, rangeEnd));

// Day key → orders on that day, for the visible window only.
export const buildDayIndex = (orders: WorkOrderListItem[], rangeStart: Dayjs, rangeEnd: Dayjs) => {
  const index = new Map<string, WorkOrderListItem[]>();
  for (const order of orders) {
    for (const day of getCalendarDays(order)) {
      if (!isDayInRange(day, rangeStart, rangeEnd)) continue;
      const key = day.format(CALENDAR_DAY_KEY_FORMAT);
      const bucket = index.get(key);
      if (bucket) bucket.push(order);
      else index.set(key, [order]);
    }
  }
  return index;
};

// Every day from FROM to TO inclusive, as ISO strings; a reversed range is put in order.
export const expandDateRange = ([from, to]: [string, string]): string[] => {
  if (!from || !to) return [];
  const [start, end] = [dayjs(from).startOf('day'), dayjs(to).startOf('day')].sort((a, b) => a.valueOf() - b.valueOf());
  const days: string[] = [];
  for (let day = start; !day.isAfter(end); day = day.add(1, 'day')) days.push(day.toISOString());
  return days;
};

// Only the calendar tile holding the selection gets its id, so a selection change re-renders at most two tiles.
export const getCellSelectedId = (orders: WorkOrderListItem[] | undefined, selectedId: number | null) =>
  selectedId !== null && orders?.some((order) => order.id === selectedId) ? selectedId : null;

// Picks dark pill text for light BE fill colours (#rrggbb).
export const isLightColor = (hex: string) => {
  const value = Number.parseInt(hex.slice(1, 7), 16);
  if (Number.isNaN(value)) return false;
  const luminance = 0.299 * (value >> 16) + 0.587 * ((value >> 8) & 0xff) + 0.114 * (value & 0xff);
  return luminance > 160;
};
